/* global ACCEPTED_ATTACHMENT_FILE_TYPES */

import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

import { Formik, Field } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';

import MilestonesEdit from '../milestones/procurement_milestones/MilestonesEdit';
import DangerAlert from '../partial/alerts/DangerAlert';

import SponsorCommitmentContract from './SponsorCommitmentContract';
import SponsorCommitmentLineTable from './SponsorCommitmentLineTable';
import SponsorImpactHeader from './SponsorImpactHeader';

import { formatMoneyWithLocale } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';
import CheckboxFormField from 'CommonComponents/CheckboxFormField';
import Tooltip from 'CommonComponents/Tooltip';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import { useToast } from 'Hooks/useToast';
import { useUsers } from 'Hooks/useUsers';

const SECTION_CASH = 'cash';
const SECTION_VIK = 'vik';

// owner_id defaults to the current user and is never empty, so it is NOT required. description is
// optional. The committing-amount and milestone gates live on the submit button (and are re-checked
// server-side), not in this schema.
const SponsorCommitSchema = Yup.object().shape({
    owner_id: Yup.string(),
    description: Yup.string(),
    supplier_id: Yup.string().required($t('This value is required')),
    contract_id: Yup.string(),
    contract_started_at: Yup.string(),
    contract_ended_at: Yup.string(),
    contract_signed: Yup.boolean(),
    contract_closed: Yup.boolean(),
});

export default function SponsorCommitForm(props) {
    const [user, setUser] = useState({});
    const [submissionDate, setSubmissionDate] = useState('');
    const [suppliers, setSuppliers] = useState([]);
    const [isAddSupplierAllowed, setIsAddSupplierAllowed] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);

    // Held as raw strings so a half-typed value ("", "1.") survives editing instead of snapping to 0.
    const [cash, setCash] = useState('');
    const [vik, setVik] = useState('');

    // Milestone section validity / autosave state lifted from MilestoneSectionForm, so the single
    // commit action can be gated on each component's phasing totalling 100%.
    const [sectionValidity, setSectionValidity] = useState({});
    const [presentSections, setPresentSections] = useState([]);
    const [milestoneSaving, setMilestoneSaving] = useState({});
    const [milestonesFailed, setMilestonesFailed] = useState(false);

    const [error, setError] = useState(null);

    const toast = useToast();
    const { users } = useUsers();

    const sponsor = props.sponsor ?? {};
    const uncommittedCash = Number(sponsor.uncommitted_cash ?? 0);
    const uncommittedVik = Number(sponsor.uncommitted_vik ?? 0);

    // Default both components to their whole remaining balance, so the screen opens pre-loaded and a
    // plain commit takes everything still outstanding on the deal.
    //
    // Clamped at 0 for the PRE-FILL only: a balance left negative before over-commitment was refused
    // would otherwise pre-fill as a positive repeat of the over-commitment, because the committing
    // input is allowNegative={false} and silently drops the minus sign — one click doubled it. The
    // uncommittedCash / uncommittedVik figures themselves stay unclamped above, so the header and the
    // line table keep showing the true negative balance.
    useEffect(() => {
        fetchSuppliers();
        setSubmissionDate(moment().format('DD/MM/YYYY'));
        setCash(String(Math.max(0, uncommittedCash)));
        setVik(String(Math.max(0, uncommittedVik)));
    }, []);

    // The Requestor defaults to the logged-in user, but the event user list (useUsers) does not
    // always contain them (e.g. a super-admin who is not an event member). Merge the current user in
    // so they are always selectable and the pre-selected owner_id resolves to a visible label.
    const requestorUsers = useMemo(() => {
        if (user && user.id && !users.some((u) => u.id === user.id)) {
            return [...users, user];
        }
        return users;
    }, [users, user]);

    const fetchSuppliers = () => {
        axios
            .get(props.sponsorCommitmentDataUrl)
            .then(({ data }) => {
                setUser({ ...data.user });
                setSuppliers([...data.suppliers]);
                setIsAddSupplierAllowed(data.add_supplier_allowed);
            })
            .catch((e) => console.log(e));
    };

    const committingCash = Number(cash) || 0;
    const committingVik = Number(vik) || 0;
    const committingValue = Math.round((committingCash + committingVik) * 100) / 100;

    const cashInvalid = committingCash < 0;
    const vikInvalid = committingVik < 0;
    // Value-only and two-component: either may be zero (a VIK-only or cash-only deal is ordinary),
    // but committing nothing at all is not.
    const amountsInvalid = cashInvalid || vikInvalid || committingValue <= 0;

    // Over-commitment is refused outright (IB-943), matching ticketing and diverging from Direct
    // Commitment and Commit Revenue: a negative balance pre-filled the screen with a positive repeat
    // of the over-commitment on reopen, so one further click doubled it. Committing exactly the
    // balance is fine — only going beyond it is refused. Mirrored server-side, which is the
    // authority; this only saves the round trip.
    //
    // The `> 0` half is load-bearing and must not be "simplified" away. On a deal whose OTHER
    // component is already negative (seeded before this rule, or by a release), that component's box
    // pre-fills 0 and a bare `committingVik > uncommittedVik` reads 0 > -1000 as an over-commit —
    // blocking an entirely legitimate cash-only commit.
    const cashOverCommitted = committingCash > 0 && committingCash > uncommittedCash;
    const vikOverCommitted = committingVik > 0 && committingVik > uncommittedVik;
    const overCommitted = cashOverCommitted || vikOverCommitted;

    // Nothing left to commit at all: with both balances at or below zero every positive figure is an
    // over-commit, so there is no amount this screen would accept. Called out separately because the
    // two boxes then pre-fill 0 and neither over-commit branch fires.
    const nothingLeft = uncommittedCash <= 0 && uncommittedVik <= 0;

    // The milestone sections this screen renders: exactly the components being committed. The server
    // serves both sections unconditionally, and that is still right for a deal carrying both
    // components: a component whose own phasing was edited away still needs somewhere to phase
    // itself. What it no longer implies is that any amount may be entered against a component — the
    // over-commit guards above bound each one to its own balance. Memoised on the committing figures
    // only, so a component dropped back to zero drops its section again.
    const sectionFilter = useMemo(() => {
        const sections = [];
        if (committingCash > 0) {
            sections.push(SECTION_CASH);
        }
        if (committingVik > 0) {
            sections.push(SECTION_VIK);
        }
        return sections;
    }, [committingCash, committingVik]);

    // EVERY section being committed has to have reported in, not just one of them. Under the older
    // `presentSections.length > 0`, typing a VIK amount onto a cash-only deal read as "loaded" while
    // sectionValidity.vik was still undefined, so Commit was disabled citing "Total percentage must
    // equal 100" against a VIK table plainly showing 100%. presentSections only ever grows, so it
    // cannot answer "is the section I need mounted yet" on its own.
    const milestonesLoaded = sectionFilter.length > 0 && sectionFilter.every((section) => presentSections.includes(section));

    // The gate is component-wise: only a component actually being committed needs its phasing to
    // total 100%. That is what lets a VIK-only deal commit without any cash phasing, while still
    // refusing a cash amount whose own phasing is short.
    const cashSectionOk = committingCash <= 0 || sectionValidity[SECTION_CASH] === true;
    const vikSectionOk = committingVik <= 0 || sectionValidity[SECTION_VIK] === true;

    // Scoped to sectionFilter — exactly the set of mounted sections — rather than the ever-growing
    // presentSections, so a component dropped back to zero stops gating the commit.
    const milestoneSaveInFlight = sectionFilter.some((section) => milestoneSaving[section] === true);

    const submitDisabled =
        amountsInvalid || overCommitted || nothingLeft || milestonesFailed || !milestonesLoaded || !cashSectionOk || !vikSectionOk || milestoneSaveInFlight;

    // The over-commit reasons come FIRST, ahead of the negative / zero-amount branch. An
    // over-committed deal pre-fills both boxes at 0, so the older ordering answered "why is Commit
    // disabled?" with "Enter a committed cash or VIK amount greater than zero" — telling the user to
    // do the one thing now forbidden.
    const blockedMessage = useMemo(() => {
        if (nothingLeft) {
            return $t('There is nothing left to commit on this sponsor. Increase the budget on this line first, then come back and commit.');
        }
        // Math.max(0, …) on the DISPLAYED figure only — see the note on the banner below.
        if (cashOverCommitted) {
            return $t(
                'You cannot commit more than the {{amount}} uncommitted cash remaining. Increase the budget on this line first, then come back and commit.',
                { amount: formatMoneyWithLocale(Math.max(0, uncommittedCash)) },
            );
        }
        if (vikOverCommitted) {
            return $t(
                'You cannot commit more than the {{amount}} uncommitted VIK remaining. Increase the budget on this line first, then come back and commit.',
                { amount: formatMoneyWithLocale(Math.max(0, uncommittedVik)) },
            );
        }
        if (cashInvalid || vikInvalid) {
            return $t('Committed cash and VIK cannot be negative.');
        }
        if (committingValue <= 0) {
            return $t('Enter a committed cash or VIK amount greater than zero.');
        }
        // Ahead of the loading branch: a failed fetch never clears it, so without this the screen
        // would sit on "Loading payment milestones…" indefinitely.
        if (milestonesFailed) {
            return $t('Payment milestones could not be loaded. Refresh the page to try again.');
        }
        if (!milestonesLoaded) {
            return $t('Loading payment milestones…');
        }
        if (!cashSectionOk || !vikSectionOk) {
            return $t('Total percentage must equal 100');
        }
        if (milestoneSaveInFlight) {
            return $t('Saving payment milestones…');
        }
        return '';
    }, [
        nothingLeft,
        cashOverCommitted,
        vikOverCommitted,
        uncommittedCash,
        uncommittedVik,
        cashInvalid,
        vikInvalid,
        committingValue,
        milestonesFailed,
        milestonesLoaded,
        cashSectionOk,
        vikSectionOk,
        milestoneSaveInFlight,
    ]);

    const handleMilestoneSavingChange = (section, isSaving) => {
        setMilestoneSaving((prev) => (prev[section] === isSaving ? prev : { ...prev, [section]: isSaving }));
    };

    const handleValidityChange = (section, isValid) => {
        setSectionValidity((prev) => (prev[section] === isValid ? prev : { ...prev, [section]: isValid }));
    };

    const handleSectionPresent = (section) => {
        setPresentSections((prev) => (prev.includes(section) ? prev : [...prev, section]));
    };

    const changeFilesHandler = (event) => {
        setSelectedFiles(event.target.files);
    };

    // Cancel must persist nothing — including the drafted milestones this screen seeded.
    const handleCancel = () => {
        const redirect = () => (window.location.href = props.backUrl);

        if (!props.cancelUrl) {
            redirect();
            return;
        }

        axios
            .put(props.cancelUrl, { sponsor_id: sponsor.id }, { headers: { ...props.mutationHeader } })
            .catch((e) => console.log(e))
            .finally(redirect);
    };

    // Requestor / submission date are portaled into the page header (right of the page title) so they
    // sit alongside the "Commit Sponsor" title while staying inside the Formik tree.
    const renderHeaderMeta = (content) => {
        const target = typeof document !== 'undefined' ? document.getElementById('sponsor-commit-header-meta') : null;
        if (target) {
            return ReactDOM.createPortal(content, target);
        }
        return <div className="tw-flex tw-justify-end tw-p-6 tw-border-b tw-border-charcoal-15">{content}</div>;
    };

    const sectionHeading = (title, suffix) => (
        <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">
            {title} {suffix && <span className="tw-text-charcoal-50 tw-font-medium tw-normal-case tw-tracking-normal">— {suffix}</span>}
        </h3>
    );

    return (
        <div className="tw-pb-8">
            <Formik
                initialValues={{
                    owner_id: props.currentUserId ?? '',
                    description: '',
                    supplier_id: '',
                    contract_id: '',
                    contract_started_at: '',
                    contract_ended_at: '',
                    contract_signed: false,
                    contract_closed: false,
                }}
                validationSchema={SponsorCommitSchema}
                onSubmit={(values, actions) => {
                    setError(null);

                    const formData = new FormData();
                    formData.append('sponsor_id', sponsor.id);
                    formData.append('owner_id', values.owner_id ?? '');
                    formData.append('description', values.description ?? '');
                    formData.append('committed_cash', '' + committingCash);
                    formData.append('committed_vik', '' + committingVik);
                    formData.append('supplier_id', values.supplier_id);
                    if (values.contract_id) {
                        formData.append('contract_id', values.contract_id);
                    }
                    formData.append('contract_started_at', values.contract_started_at ? moment(values.contract_started_at).format('YYYY-MM-DD') : '');
                    formData.append('contract_ended_at', values.contract_ended_at ? moment(values.contract_ended_at).format('YYYY-MM-DD') : '');
                    formData.append('contract_signed', values.contract_signed ? '1' : '0');
                    formData.append('contract_closed', values.contract_closed ? '1' : '0');

                    for (let i = 0; i < selectedFiles.length; i++) {
                        formData.append(`files[${i}]`, selectedFiles[i]);
                    }

                    axios
                        .post(props.storeUrl, formData, { headers: { ...props.mutationHeader } })
                        .then(() => {
                            toast.success($t('Sponsorship committed successfully'));
                            setTimeout(() => (window.location.href = props.backUrl), 2000);
                        })
                        .catch((e) => {
                            console.log(e);
                            setError(e?.response?.data?.errors);
                            e?.response?.data?.errors && actions.setErrors(e.response.data.errors);
                        })
                        .finally(() => actions.setSubmitting(false));
                }}
            >
                {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                    <div className="card front bg-white border-0 rounded-15px shadow tw-overflow-hidden">
                        {renderHeaderMeta(
                            <div className="tw-flex tw-items-start tw-gap-6">
                                <div>
                                    <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-font-semibold tw-mb-1">
                                        {$t('Requestor')}
                                    </div>
                                    <div className="tw-min-w-[12rem]">
                                        <UserSelectorInput
                                            selectedId={values.owner_id}
                                            usersList={requestorUsers}
                                            onChange={(value) => setFieldValue('owner_id', value)}
                                            fetchUrl={props.listUsersUrl}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-font-semibold tw-mb-1">
                                        {$t('Submission Date')}
                                    </div>
                                    <div className="tw-text-sm tw-font-semibold tw-text-charcoal tw-pt-2">{submissionDate}</div>
                                </div>
                            </div>,
                        )}

                        {/* Hero: cash / VIK uncommitted impact. The split is mandatory here. */}
                        <div className="tw-p-6 tw-border-b tw-border-charcoal-15">
                            <SponsorImpactHeader
                                context={{ crumbs: props.lineCrumbs, line: sponsor.name }}
                                now={{ cash: uncommittedCash, vik: uncommittedVik }}
                                committing={{ cash: committingCash, vik: committingVik }}
                                blocked={overCommitted || nothingLeft}
                            />
                        </div>

                        {/* Body: two columns. Splits at xl (not lg, which DirectCommitForm uses): the
                            sidebar eats ~300px, so between lg and xl two columns force the milestone
                            table wider than the viewport and the whole card overflows horizontally.
                            min-w-0 lets each column shrink below its content width. */}
                        <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-6 tw-p-6">
                            {/* LEFT: commitment line + cash and VIK payment milestones */}
                            <div className="tw-flex tw-flex-col tw-gap-y-6 tw-min-w-0">
                                <section className="tw-min-w-0 tw-overflow-x-auto">
                                    {sectionHeading($t('Commitment'), $t('edit the committing cash and VIK inline'))}
                                    <SponsorCommitmentLineTable
                                        line={{ name: sponsor.name, uncommittedCash, uncommittedVik }}
                                        cash={cash}
                                        vik={vik}
                                        cashInvalid={cashInvalid || cashOverCommitted}
                                        vikInvalid={vikInvalid || vikOverCommitted}
                                        committingValue={committingValue}
                                        onCashChange={setCash}
                                        onVikChange={setVik}
                                    />
                                </section>

                                {/* overflow-x-auto: the shared milestone table has a real minimum width
                                    (drag handle, date, description, %, value, delete) — if a viewport is
                                    ever too narrow for it, it scrolls inside this section rather than
                                    stretching the page. */}
                                <section className="tw-min-w-0 tw-overflow-x-auto">
                                    {sectionHeading($t('Payment Milestones'), $t('inherited from the sponsor line, editable'))}
                                    {/* vikEnabled is unconditionally true and hideSectionHeader is off:
                                        sponsorship renders a section per component it is committing
                                        (sectionFilter), and with two possible tables the per-section
                                        heading is the only thing naming Cash vs VIK.
                                        isRecalculateTotalAllowed is left at its default false so each
                                        section's value base comes from the committing figures rather
                                        than the sponsor's stored balances. */}
                                    <MilestonesEdit
                                        sectionFilter={sectionFilter}
                                        getMilestonesUrl={props.getSponsorMilestonesUrl}
                                        minDate={props.minDate}
                                        maxDate={props.maxDate}
                                        parentSection="sponsor-commit"
                                        hideSectionHeader={false}
                                        totalValue={committingValue}
                                        committingCash={committingCash}
                                        committingVik={committingVik}
                                        vikEnabled={true}
                                        isSliderAllowed={false}
                                        allowConfirm={false}
                                        preserveDraftMilestones={true}
                                        showCancelButton={false}
                                        onValidityChange={handleValidityChange}
                                        onSectionPresent={handleSectionPresent}
                                        onAutosaveStateChange={handleMilestoneSavingChange}
                                        onLoadErrorChange={setMilestonesFailed}
                                    />
                                </section>
                            </div>

                            {/* RIGHT: sponsor + tier + description + attachments */}
                            <div className="tw-flex tw-flex-col tw-gap-y-6 tw-min-w-0">
                                <section>
                                    {sectionHeading($t('Sponsor'))}
                                    <SponsorCommitmentContract
                                        suppliers={suppliers}
                                        isAddSupplierAllowed={isAddSupplierAllowed}
                                        errors={errors}
                                        touched={touched}
                                        values={values}
                                        onSupplierCreated={fetchSuppliers}
                                        suppliersIndexUrl={props.suppliersIndexUrl}
                                        suppliersStoreUrl={props.suppliersStoreUrl}
                                        suppliersCreateUrl={props.suppliersCreateUrl}
                                        sponsorName={sponsor.name}
                                        setFieldValue={setFieldValue}
                                    />
                                    <div className="tw-flex tw-gap-x-8 tw-mt-4">
                                        <CheckboxFormField name="contract_signed" id="contract_signed" type="checkbox" label={$t('Contract Signed')} />
                                        <CheckboxFormField name="contract_closed" id="contract_closed" type="checkbox" label={$t('Contract Closed')} />
                                    </div>
                                </section>

                                {/* Tier and reference belong to the sponsor line, not to this
                                    commitment, so they are shown for context and never editable here —
                                    re-tiering a sponsor mid-commit would silently regroup it in every
                                    tier-based report. */}
                                <section>
                                    {sectionHeading($t('Sponsorship package'), $t('managed on the sponsor'))}
                                    <div className="tw-rounded-lg tw-bg-grey/50 tw-px-4 tw-py-3 tw-grid tw-grid-cols-2 tw-gap-4">
                                        <div>
                                            <div className="tw-text-xs tw-text-charcoal-50 tw-font-semibold tw-mb-1">{$t('Tier')}</div>
                                            <div className="tw-text-sm tw-font-semibold tw-text-charcoal">{props.sponsorTier || '-'}</div>
                                        </div>
                                        <div>
                                            <div className="tw-text-xs tw-text-charcoal-50 tw-font-semibold tw-mb-1">{$t('Reference')}</div>
                                            <div className="tw-text-sm tw-font-semibold tw-text-charcoal">{sponsor.reference_number || '-'}</div>
                                        </div>
                                    </div>

                                    {/* This sponsor's value feeds linked expense quantities. Committing
                                        never changes that value, so this is purely informational — but
                                        it tells the user the deal has downstream consumers. */}
                                    {props.dependentsCount > 0 && (
                                        <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-blue-15 tw-bg-blue-10 tw-px-4 tw-py-3">
                                            <div className="tw-text-xs tw-font-semibold tw-text-charcoal">
                                                {$t('Source for {{count}} linked expense quantities', { count: props.dependentsCount })}
                                            </div>
                                            {!!props.dependentNames?.length && (
                                                <div className="tw-text-xs tw-text-charcoal-75 tw-mt-1">{props.dependentNames.join(', ')}</div>
                                            )}
                                            <div className="tw-text-xs tw-text-charcoal-50 tw-mt-1">
                                                {$t('Committing does not change the sponsorship value, so these stay as they are.')}
                                            </div>
                                        </div>
                                    )}
                                </section>

                                <section>
                                    <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">
                                        {$t('Commitment description')}{' '}
                                        <span className="tw-text-charcoal-50 tw-font-medium tw-normal-case tw-tracking-normal tw-bg-charcoal-15 tw-rounded tw-px-1.5 tw-py-0.5">
                                            {$t('Optional')}
                                        </span>
                                    </h3>
                                    <Field
                                        as="textarea"
                                        className="form-control !tw-bg-grey/50 tw-border-0 tw-rounded-lg"
                                        name="description"
                                        rows={3}
                                        placeholder={$t('Please provide an overview of the sponsorship commitment here')}
                                    />
                                </section>

                                <section>
                                    {sectionHeading($t('Attachments'))}
                                    <input
                                        id="sponsor-commitment-attachments"
                                        type="file"
                                        name="files[]"
                                        multiple
                                        className="file-input"
                                        placeholder={$t('Select Files')}
                                        accept={typeof ACCEPTED_ATTACHMENT_FILE_TYPES !== 'undefined' ? ACCEPTED_ATTACHMENT_FILE_TYPES : undefined}
                                        onChange={changeFilesHandler}
                                    />
                                </section>
                            </div>
                        </div>

                        {/* Per component rather than one combined line: a deal can be over-committing
                            on cash and VIK at once, and the user needs to know which figure to bring
                            down. Commit is disabled while any of these shows.

                            Math.max(0, …) on the DISPLAYED figure only, never on the guards or on
                            the header and line table above, which must keep showing the true
                            balance. The breaching component can itself already be negative — a deal
                            with cash left and VIK at -1,000 is exactly the mixed state this ticket
                            exists to handle — and "more than the -£1,000 uncommitted VIK remaining"
                            is nonsense. There is nothing left, so £0 is what the user needs to
                            read. Mirrored server-side in SponsorCommitmentController.

                            The per-component blocks are gated on !nothingLeft so this panel follows
                            the same precedence as blockedMessage: on a 0/0 sponsor "nothing left" is
                            the whole story, and adding "you cannot commit more than the £0
                            uncommitted cash remaining" underneath it just says the same thing twice
                            while disagreeing with the tooltip. */}
                        {(overCommitted || nothingLeft) && (
                            <div className="tw-mx-6 tw-mb-2 tw-rounded-lg tw-border tw-border-red tw-bg-red-10 tw-px-4 tw-py-3 tw-text-sm tw-text-red tw-font-medium">
                                {nothingLeft && (
                                    <div>
                                        {$t(
                                            'There is nothing left to commit on this sponsor. Increase the budget on this line first, then come back and commit.',
                                        )}
                                    </div>
                                )}
                                {!nothingLeft && cashOverCommitted && (
                                    <div>
                                        {$t(
                                            'You cannot commit more than the {{amount}} uncommitted cash remaining. Increase the budget on this line first, then come back and commit.',
                                            { amount: formatMoneyWithLocale(Math.max(0, uncommittedCash)) },
                                        )}
                                    </div>
                                )}
                                {!nothingLeft && vikOverCommitted && (
                                    <div>
                                        {$t(
                                            'You cannot commit more than the {{amount}} uncommitted VIK remaining. Increase the budget on this line first, then come back and commit.',
                                            { amount: formatMoneyWithLocale(Math.max(0, uncommittedVik)) },
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Every field's messages, not just the first — a milestones or budget
                            rejection arriving alongside a field error must not be swallowed. */}
                        {!!error && _.isObject(error) && (
                            <div className="tw-px-6">
                                {Object.entries(error).flatMap(([field, messages]) =>
                                    (Array.isArray(messages) ? messages : [messages]).map((e, i) => <DangerAlert key={`${field}-${i}`} message={e} />),
                                )}
                            </div>
                        )}

                        {/* Action bar: single commit action */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-p-6 tw-border-t tw-border-charcoal-15 tw-bg-charcoal-10">
                            <p className="tw-text-xs tw-text-charcoal-50 tw-max-w-xl tw-mb-0">
                                {$t(
                                    'The full audit trail — user, timestamp, committed cash and VIK, and prior versus new uncommitted — is captured on submit.',
                                )}
                            </p>
                            <div className="tw-flex tw-items-center tw-gap-x-3">
                                {/* The reason Commit is disabled, on the screen rather than only in the
                                    button's tooltip: a disabled button with no visible explanation is
                                    what left the user stuck. Rendered unconditionally and emptied when
                                    there is nothing to say — a live region mounted together with its
                                    text is generally not announced, only mutations of an already-present
                                    one are. aria-describedby gives a keyboard user the same reason on
                                    focus, which the pointer-only tooltip cannot. */}
                                <p
                                    id="sponsor-commit-blocked-reason"
                                    className="tw-text-xs tw-font-medium tw-text-red tw-mb-0 tw-max-w-xs tw-text-right"
                                    role="status"
                                >
                                    {submitDisabled ? blockedMessage : ''}
                                </p>
                                <Button variant="secondary" disabled={isSubmitting} onClick={handleCancel}>
                                    {$t('Cancel')}
                                </Button>
                                <Tooltip placement="top" tooltipType="text" tooltipContent={blockedMessage} allowTooltip={submitDisabled}>
                                    <span className="tw-inline-block">
                                        <Button
                                            variant="primary"
                                            disabled={isSubmitting || submitDisabled}
                                            onClick={handleSubmit}
                                            aria-describedby="sponsor-commit-blocked-reason"
                                        >
                                            {$t('Commit Sponsor')}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                )}
            </Formik>
        </div>
    );
}
